import { SHOWCASE_DESIGN_CONTRACT_VERSION } from "../../../lib/showcaseDesignContract";
import cssContract from "./flowDesignCssContract.json";

const FLOW_DESIGN_PARSE_REASONS = new Set([
  "utf8",
  "parse_error",
  "empty_stylesheet",
]);

export type FlowDesignValidationDiagnostic = {
  reason: string;
  message: string;
  repairPrompt: string;
};

type ErrorEnvelope = {
  code?: unknown;
  message?: unknown;
  details?: unknown;
};

export function flowDesignValidationDiagnostic(
  error: unknown,
): FlowDesignValidationDiagnostic | null {
  const envelope = errorEnvelope(error);
  if (envelope?.code !== "flow_design_invalid_css") return null;
  const details = isRecord(envelope.details) ? envelope.details : null;
  const reason = details?.reason;
  if (typeof reason !== "string" || !FLOW_DESIGN_PARSE_REASONS.has(reason)) {
    return null;
  }
  const explanation = validationExplanation(reason);
  return {
    reason,
    message: `${explanation} Код проверки: ${reason}.`,
    repairPrompt: buildRepairPrompt(reason),
  };
}

export function flowDesignErrorMessage(
  error: unknown,
  fallback: string,
): string {
  const diagnostic = flowDesignValidationDiagnostic(error);
  if (diagnostic) return diagnostic.message;
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error) return error;
  if (isRecord(error) && typeof error.detail === "string" && error.detail) {
    return error.detail;
  }
  const envelope = errorEnvelope(error);
  if (typeof envelope?.message === "string" && envelope.message) {
    return envelope.message;
  }
  return typeof envelope?.code === "string" && envelope.code
    ? envelope.code
    : fallback;
}

function validationExplanation(reason: string): string {
  if (reason === "utf8") {
    return "CSS не принят: содержимое файла не является корректным UTF-8";
  }
  if (reason === "empty_stylesheet") {
    return "CSS не принят: файл пуст или не содержит ни одного CSS-правила";
  }
  if (reason === "parse_error") {
    return "CSS не принят: parser обнаружил незавершённый блок, строку, url() или другую синтаксическую ошибку";
  }
  return "CSS не принят: файл не прошёл техническую проверку";
}

function buildRepairPrompt(reason: string): string {
  return [
    "Продолжи работу в этом же диалоге и исправь приложенный style.css для trafsy, сохранив ранее подтверждённый визуальный brief.",
    `DOM contract v${SHOWCASE_DESIGN_CONTRACT_VERSION}; CSS policy v${cssContract.cssPolicyVersion}.`,
    `Authoritative validator отклонил файл: reason=${reason}.`,
    "Проверь заново ВЕСЬ файл по полному prompt trafsy, а не только первую найденную ошибку.",
    "Исправь кодировку или CSS-синтаксис: файл должен быть непустым UTF-8 stylesheet с хотя бы одним корректно разобранным top-level rule, без NUL, незакрытых blocks/strings/functions, bad-url и bad-string.",
    "CSS policy v3 разрешает произвольные стандартные selectors, declarations, !important, pseudo-elements, at-rules, external URL, images и fonts: не удаляй их только из-за старых ограничений.",
    "Верни ровно один исправленный UTF-8 файл style.css либо один css code block без текста до и после.",
  ].join("\n");
}

function errorEnvelope(error: unknown): ErrorEnvelope | null {
  if (!isRecord(error)) return null;
  if (isRecord(error.error)) return error.error;
  if (isRecord(error.detail)) return error.detail;
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
